import type React from 'react'
import type { JSX, MouseEvent } from 'react'
import type { IImagePoster } from 'atoms/ImagePoster'
import type { BANNER_BACK_BUTTON_INDENT } from 'vars/index'
import type {
  PartnerBannerCursor,
  PartnerBannerOverlayDirection,
} from './PartnerBanner.h'

import { useEffect, useMemo, useState } from 'react'
import { FallbackImageSizes } from 'atoms/FallbackImage'
import { ImagePoster } from 'atoms/ImagePoster'
import {
  PartnerProgramCoverDesktop,
  PartnerProgramCoverMobile,
} from 'atoms/PartnerProgramCover'
import { CardCorners } from 'blocks/Cards'
import { BannerSkeleton } from 'blocks/Skeletons'
import { isTouchDevice } from 'utils/helpers'
import { useClientWidth, useOrientation } from 'utils/hooks'
import { BANNER_OVERLAY_WIDTH } from 'vars/banner-overlay-width'
import { BANNER_ASPECT_RATIO, BANNER_PADDINGS, BANNER_WIDTH } from 'vars/index'
import {
  PartnerBannerAlignItems,
  PartnerBannerJustifyContent,
  PartnerBannerOverlay,
} from './PartnerBanner.h'
import {
  BannerContent,
  FallbackCover,
  BannerSC,
  Image,
  OverlayLayout,
} from './PartnerBanner.styled'

export interface IPartnerBannerProps {
  children?: React.ReactNode
  width?: BANNER_WIDTH
  aspectRatio?: BANNER_ASPECT_RATIO
  justifyContent?: PartnerBannerJustifyContent
  alignItems?: PartnerBannerAlignItems
  overlay?: PartnerBannerOverlay | undefined
  overlayHorizontal?: PartnerBannerOverlay | undefined
  overlayVertical?: PartnerBannerOverlay | undefined
  overlayDirection?: PartnerBannerOverlayDirection | undefined
  overlayEnabled?: boolean
  overlayWidth?: number | undefined
  overlayHeight?: number | undefined
  posterUrl: string
  posterMobileUrl: string
  paddings?: BANNER_PADDINGS
  backButtonIndent?: BANNER_BACK_BUTTON_INDENT
  fallbackImageSize?: IImagePoster['fallbackImageSize']
  isCoverFallbackImage?: boolean
  cursor?: PartnerBannerCursor
  className?: string
  style?: React.CSSProperties
  onClick?: (event: MouseEvent) => void
  corners?: CardCorners
  isPartialPoster?: boolean
  noContentInside?: boolean
  singleBanner?: any
  loading?: boolean
}

export const PartnerBanner = ({
  children,
  width = BANNER_WIDTH.SIZE_100,
  aspectRatio = BANNER_ASPECT_RATIO.SIZE_10,
  justifyContent = PartnerBannerJustifyContent.Center,
  alignItems = PartnerBannerAlignItems.Start,
  overlay = PartnerBannerOverlay.SURFACE_OPACITY_DARK_40,
  overlayHorizontal = PartnerBannerOverlay.BANNER_PARTIAL_HORIZONTAL_OVERLAY,
  overlayVertical = PartnerBannerOverlay.BANNER_VERTICAL_OVERLAY,
  overlayDirection,
  overlayEnabled = true,
  overlayHeight,
  overlayWidth,
  posterUrl = '',
  posterMobileUrl = '',
  paddings = BANNER_PADDINGS.SIZE_10,
  backButtonIndent,
  fallbackImageSize,
  cursor,
  onClick,
  className = '', // some props for Linaria
  style,
  corners = CardCorners.CORNERS_0,
  isCoverFallbackImage,
  isPartialPoster = true,
  noContentInside,
  singleBanner,
  loading = false,
}: IPartnerBannerProps): JSX.Element => {
  const clientWidth = useClientWidth()
  const { orientation } = useOrientation()
  const [posterError, setPosterError] = useState(false)

  const isMobile = clientWidth > 0 && clientWidth < 1240

  const mobileLandscape =
    clientWidth >= 600 &&
    clientWidth < 1240 &&
    isTouchDevice() &&
    orientation === 'landscape'

  const poster = isMobile
    ? mobileLandscape
      ? posterUrl
      : posterMobileUrl
    : posterUrl

  // update poster error when poster changes
  useEffect(() => {
    setPosterError(false)
  }, [poster])

  const partialTemplate =
    isPartialPoster &&
    (width === BANNER_WIDTH.SIZE_100 ||
      width === BANNER_WIDTH.SIZE_94 ||
      singleBanner)

  const bannerOverlayWidth = singleBanner
    ? BANNER_OVERLAY_WIDTH.SIZE_60
    : overlayWidth

  const partialAspectRatio = partialTemplate ? BANNER_ASPECT_RATIO.POSTER : null

  const ovelayLayoutTemplate = useMemo(
    () => (
      <OverlayLayout
        overlay={overlay}
        overlayHorizontal={overlayHorizontal}
        overlayVertical={overlayVertical}
        overlayDirection={overlayDirection}
        overlayEnabled={overlayEnabled}
        overlayWidth={bannerOverlayWidth}
        overlayHeight={overlayHeight}
        aspectRatio={partialAspectRatio}
      />
    ),
    [
      overlay,
      overlayHorizontal,
      overlayVertical,
      overlayDirection,
      overlayEnabled,
      bannerOverlayWidth,
      overlayHeight,
      partialAspectRatio,
    ]
  )

  const bannerContnerTemplate = useMemo(
    () => (
      <BannerContent
        justifyContent={justifyContent}
        alignItems={alignItems}
        paddings={paddings}
        backButtonIndent={backButtonIndent}
      >
        {children}
      </BannerContent>
    ),
    [justifyContent, alignItems, paddings, backButtonIndent, children]
  )

  const posterTemplate = useMemo(() => {
    if (loading && !poster) {
      return clientWidth !== 0 ? (
        <Image
          aspectRatio={partialAspectRatio}
          noContentInside={noContentInside}
        >
          <BannerSkeleton aspectRatio={aspectRatio} bannerWidth={width} />
        </Image>
      ) : (
        <></>
      )
    }

    if (isCoverFallbackImage && (!poster || posterError)) {
      return (
        <Image>
          <FallbackCover>
            <PartnerProgramCoverDesktop />
            <PartnerProgramCoverMobile />
          </FallbackCover>
          {ovelayLayoutTemplate}
          {bannerContnerTemplate}
        </Image>
      )
    }

    return clientWidth !== 0 ? (
      <Image aspectRatio={partialAspectRatio} noContentInside={noContentInside}>
        <ImagePoster
          posterUrl={poster}
          alt='BannerImage'
          fallbackImageSize={
            fallbackImageSize ??
            (width === BANNER_WIDTH.SIZE_66
              ? FallbackImageSizes.M
              : FallbackImageSizes.XXL)
          }
          corners={corners}
          lazy={false}
          handleError={() => setPosterError(true)}
        />
        {ovelayLayoutTemplate}
        {bannerContnerTemplate}
      </Image>
    ) : (
      <></>
    )
  }, [
    clientWidth,
    poster,
    fallbackImageSize,
    width,
    corners,
    partialAspectRatio,
    aspectRatio,
    ovelayLayoutTemplate,
    posterError,
    setPosterError,
    isCoverFallbackImage,
    noContentInside,
    loading,
  ])

  return (
    <BannerSC
      className={className}
      style={style}
      width={width}
      aspectRatio={aspectRatio}
      cursor={cursor}
      onClick={onClick}
      corners={corners}
    >
      {posterTemplate}
      {ovelayLayoutTemplate}
      {bannerContnerTemplate}
    </BannerSC>
  )
}
